import { ReactElement } from 'react';

import { Box } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { DefaultLayout, Footer } from '@/components/layouts';
import { ArticleContent } from '@/components/sections/blog';
import { CaseStudyHeader, CaseStudyRecommended } from '@/components/sections/case-studies';
import { LetsBuildSomethingSection } from '@/components/sections/common';
import { DynamicBlockSelector } from '@/components/sections/dynamic-blocks/DynamicBlockSelector';
import {
  PropsBuilder,
  getCaseStudiesData,
  getPublicationState,
  useGetCaseStudyData,
  useGetProposedCaseStudiesData,
} from '@/graphql/api';
import {
  useGetCaseStudyBySlugQuery,
  useGetGlobalQuery,
  useGetMenuQuery,
  useGetProposedCaseStudiesQuery,
} from '@/graphql/generated';
import { NextPageWithLayout } from '@/pages/_app';
import { DEFAULT_LOCALE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';

interface CaseStudyProps {
  preview: boolean;
  slug: string;
}

const CaseStudy: NextPageWithLayout<CaseStudyProps> = ({ preview, slug }) => {
  const {
    title,
    titleMarkdown,
    descriptionMarkdown,
    caseStudyTags,
    image,
    contentBlocks,
    client,
    clientTooltip,
    location,
    projectStatus,
    projectStartDate,
    website,
  } = useGetCaseStudyData(slug, preview, 'all');
  const recommendedCaseStudies = useGetProposedCaseStudiesData(slug);

  return (
    <>
      <Global styles={{ html: { scrollBehavior: 'smooth' } }} />
      <CaseStudyHeader
        title={titleMarkdown || title}
        description={descriptionMarkdown}
        coverImage={image}
        client={client}
        clientTooltip={clientTooltip}
        location={location}
        website={website}
        tags={caseStudyTags}
        projectStatus={projectStatus}
        projectStartDate={projectStartDate}
      />
      {Array.isArray(contentBlocks)
        ? contentBlocks.map((block, index) => {
            if (block?.__typename === 'ComponentCommonComponentsCkEditor') {
              return (
                <Box key={block.id} px={{ base: 5, sm: 6, lg: 20, '3xl': 80 }} py="40px">
                  <ArticleContent maxW="container.lg" content={block.richText} videoWidth={'80%'} />
                </Box>
              );
            }

            return (
              <DynamicBlockSelector key={`${block?.__typename}-${index}`} typename={block?.__typename} {...block} />
            );
          })
        : null}
      <CaseStudyRecommended caseStudies={recommendedCaseStudies} />
      <LetsBuildSomethingSection overflow={false} />
    </>
  );
};

CaseStudy.getLayout = (page: ReactElement) => {
  const { metadata, menu, preview } = page.props;

  return (
    <DefaultLayout metadata={metadata} preview={preview} menu={menu}>
      {page}
      <Footer menu={menu} />
    </DefaultLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.ENV === 'staging') {
    return { paths: [], fallback: 'blocking' };
  }

  const caseStudies = await getCaseStudiesData();
  const paths = caseStudies!.map((caseStudy) => ({
    params: { slug: caseStudy.attributes?.slug },
  }));

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { params, locale, preview = false } = context;
  const slug = params?.slug as string;

  const propsBuilder = new PropsBuilder();

  const publicationState = getPublicationState(preview);
  const variables = {
    slug,
    publicationState,
    locale: 'all', // by default, Strapi creates unique "slag" for each localization version
  };

  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });

  const result = await propsBuilder.withQuery(useGetCaseStudyBySlugQuery, {
    variables,
    getMetadata: (data) => data.caseStudies?.data[0]?.attributes?.metadata,
    isPrivate: preview,
  });

  propsBuilder.withQuery(useGetProposedCaseStudiesQuery, {
    variables: {
      slug,
    },
  });
  propsBuilder.withQuery(useGetMenuQuery, {
    getMenu: (data) => data.menu,
  });

  const caseStudy = result.caseStudies?.data[0];

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON, LOCALES.CASE_STUDY]);

  if (!caseStudy) {
    return {
      notFound: true,
      revalidate: PAGE_REVALIDATE_TIME,
    };
  }

  return {
    props: {
      ...(await propsBuilder.build(lang)),
      preview,
      slug,
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default CaseStudy;
